/**
 * Generic horizontal-scroll indicator: a thin track + thumb showing how much of a `.scroll-area`
 * sibling is scrolled, so users on touch devices realize a row can be swiped further. Wraps around any
 * existing `.scroll-area` element - no changes to the scroll area itself, no scroll-snap/carousel logic.
 * Self-hides via the `is-hidden` class whenever the content doesn't actually overflow (e.g. on wide
 * screens, or when a section's "stack on mobile" layout wraps instead of scrolling).
 */
class FeschdScrollIndicator extends HTMLElement {
  connectedCallback() {
    this._scrollArea = this.querySelector('.scroll-area') || this.firstElementChild;
    if (!this._scrollArea) return;

    this._track = document.createElement('div');
    this._track.className = 'feschd-scroll-indicator__track';
    this._thumb = document.createElement('div');
    this._thumb.className = 'feschd-scroll-indicator__thumb';
    this._track.append(this._thumb);
    this.append(this._track);

    this._onScroll = () => this.update();
    this._scrollArea.addEventListener('scroll', this._onScroll, { passive: true });

    this._resizeObserver = new ResizeObserver(() => this.update());
    this._resizeObserver.observe(this._scrollArea);

    this.update();
  }

  disconnectedCallback() {
    this._scrollArea?.removeEventListener('scroll', this._onScroll);
    this._resizeObserver?.disconnect();
  }

  update() {
    const area = this._scrollArea;
    const overflow = area.scrollWidth - area.clientWidth;
    const scrollable = overflow > 1;
    this.classList.toggle('is-hidden', !scrollable);
    if (!scrollable) return;

    const trackWidth = this._track.clientWidth;
    const thumbWidth = Math.max(24, (area.clientWidth / area.scrollWidth) * trackWidth);
    const maxThumbOffset = trackWidth - thumbWidth;
    const scrollRatio = area.scrollLeft / overflow;

    this._thumb.style.width = `${thumbWidth}px`;
    this._thumb.style.transform = `translateX(${scrollRatio * maxThumbOffset}px)`;
  }
}

if (!customElements.get('feschd-scroll-indicator')) {
  customElements.define('feschd-scroll-indicator', FeschdScrollIndicator);
}
